import { Directive, inject, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';

@Directive({
  selector: 'kendo-numerictextbox[cueNumericSpinnerChange]',
  host: {
    '(pointerdown)': 'onPointerDown($event)',
    '(document:pointerup)': 'stopSpinning()',
    '(document:pointercancel)': 'stopSpinning()',
    '(pointerleave)': 'stopSpinning()',
    '(window:blur)': 'stopSpinning()',
  },
})
export class NumericSpinnerChangeDirective {
  private readonly numericInput = inject(NumericTextBoxComponent);
  private pressedSpinner: Element | null = null;

  readonly cueNumericSpinnerChange = output<number | null>();

  constructor() {
    // Commit spinner changes as they happen, while leaving typed input to blur/Enter.
    this.numericInput.valueChange.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (this.pressedSpinner) {
        this.cueNumericSpinnerChange.emit(value);
      }
    });
  }

  protected onPointerDown(event: PointerEvent): void {
    const target = event.target;
    if (
      event.button !== 0 ||
      !event.isPrimary ||
      this.numericInput.disabled ||
      this.numericInput.readonly ||
      !(target instanceof Element)
    ) {
      return;
    }

    this.pressedSpinner = target.closest('.k-spinner-increase, .k-spinner-decrease');
    if (!this.pressedSpinner) {
      return;
    }

    event.stopPropagation();
    if (event.pointerType !== 'mouse') {
      // Kendo's spinner listens to mouse events; bridge touch and pen before compatibility clicks.
      event.preventDefault();
      this.pressedSpinner.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 }));
    }
  }

  protected stopSpinning(): void {
    const spinner = this.pressedSpinner;
    this.pressedSpinner = null;
    // Stop Kendo's timer on pointer cancellation and window blur as well as release.
    spinner?.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
  }
}
